import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import './Technology.css';
import { Brain, Microscope, Activity, Circle } from 'lucide-react';
import 'particles.js/particles';

const steps = [
  {
    title: "Initial Evaluation",
    description: "We record your brain activity patterns using electroencephalogram (EEG) technology. This establishs a baseline assessment of your neural function and identifies areas requiring treatment",
    icon: Brain,
    details: {
      overview: "The evaluation process begins with a comprehensive EEG recording session",
      keyPoints: [
        "High-resolution EEG data collection",
        "Analysis of brain wave patterns across different frequency bands",
        "Identification of areas showing irregular activity",
        "Baseline documentation for treatment planning"
      ],
      duration: "30 minutes",
      whatToExpect: "During this non-invasive procedure, you'll be comfortably seated while we place EEG sensors on your scalp to record your brain's electrical activity."
    }
  },
  {
    title: "Analysis & Protocol Design",
    description: "Expert Clinicans and Trained Doctors leverage Advanced AI analysis to transform your EEG data into a customized treatment plan",
    icon: Microscope,
    details: {
      overview: "Expert Clinicans and Trained Doctors leverage Advanced AI analysis to transform your EEG data into a customized treatment plan",
      keyPoints: [
        "AI-powered comparison with normative databases",
        "Identification of optimal stimulation parameters",
        "Development of personalized Variable Pulse Interval (VPI) patterns",
        "Creation of custom targeted treatment protocols"
      ],
      duration: "30min - 1 hour",
      whatToExpect: "Our team will process your EEG data, debrief the analysis and results with you, then develop a customized treatment protocol tailored to your specific needs."
    }
  },
  {
    title: "Treatment Delivery",
    description: "We deliver customized EMBP™ with your precisely matched VPI patterns to optimize neural function and address identified abnormalities",
    icon: Activity,
    details: {
      overview: "Delivery of personalized electromagnetic stimulation using advanced EMBP™ technology.",
      keyPoints: [
        "Precise targeting of identified brain regions",
        "Custom-matched VPI stimulation patterns",
        "Non-invasive electromagnetic pulses",
        "Real-time monitoring during treatment"
      ],
      duration: "30-40 minutes per session",
      whatToExpect: "Treatment sessions are conducted daily (Monday-Friday). You'll be seated comfortably while receiving the non-invasive therapy."
    }
  },
  {
    title: "Progress Monitoring",
    description: "We conduct regular EEG assessments to track treatment efficacy and dynamically adjust protocols based on neural response and clinical improvement",
    icon: Circle,
    details: {
      overview: "Ongoing assessment and protocol refinement to optimize treatment outcomes",
      keyPoints: [
        "Regular progress EEG recordings",
        "Comparison with baseline measurements",
        "Dynamic protocol adjustments",
        "Tracking of clinical improvements"
      ],
      duration: "Throughout treatment course",
      whatToExpect: "We'll conduct periodic EEG recordings to track your progress and make any necessary adjustments to your treatment protocol."
    }
  }
];

function ProcessSteps({ steps }) {
  const [active, setActive] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isInView) return;  // Only handle keypresses when section is in view
      
      if (e.key === 'ArrowLeft' || e.key === '<') {
        setActive((prev) => (prev - 1 + steps.length) % steps.length);
      } else if (e.key === 'ArrowRight' || e.key === '>') {
        setActive((prev) => (prev + 1) % steps.length);
      }
    };

    // Check if section is in view
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.2 }  // Trigger when 20% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Add keyboard event listener
    window.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      observer.disconnect();
    };
  }, [steps.length, isInView]);

  // Add this useEffect function to handle mobile accordion behavior
  useEffect(() => {
    // Only apply this on mobile
    if (window.innerWidth <= 768) {
      // Get all step elements
      const stepElements = document.querySelectorAll('.embp-step');
      
      // Function to handle step click
      const handleStepClick = (event) => {
        // Get the clicked step
        const clickedStep = event.currentTarget;
        
        // Check if this step is already active
        const isActive = clickedStep.classList.contains('active');
        
        // Remove active class from all steps
        stepElements.forEach(step => {
          step.classList.remove('active');
        });
        
        // If the clicked step wasn't active, make it active
        if (!isActive) {
          clickedStep.classList.add('active');
        }
      };
      
      // Add click event listeners to all steps
      stepElements.forEach(step => {
        step.addEventListener('click', handleStepClick);
      });
      
      // Clean up event listeners when component unmounts
      return () => {
        stepElements.forEach(step => {
          step.removeEventListener('click', handleStepClick);
        });
      };
    }
  }, []); // Empty dependency array so this runs once on component mount

  return (
    <div className="process-steps-container" ref={sectionRef}>
      <div className="process-steps-content">
        {/* Desktop version */}
        <div className="steps-list desktop-steps">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div 
                key={idx}
                className={`step-item ${active === idx ? 'active' : ''}`}
                onClick={() => setActive(idx)}
              >
                <div className="step-icon">
                  <Icon />
                </div>
                <div className="step-text">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile version - timeline style */}
        <div className="mobile-steps">
          <div className="mobile-timeline-line"></div>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div 
                className={`embp-step ${active === index ? 'active' : ''}`} 
                onClick={() => setActive(index)}
              >
                <h3 className="embp-step-title">
                  <span className="step-number">{index + 1}</span>
                  {step.title}
                </h3>
              </div>
              
              {/* Mobile-only content container that appears below each step */}
              <div className="mobile-step-content">
                <p>{step.description}</p>
                {step.details && (
                  <>
                    <h4>Overview</h4>
                    <p>{step.details.overview}</p>
                    
                    <h4>Key Points</h4>
                    <ul>
                      {step.details.keyPoints.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                    
                    <h4>Duration</h4>
                    <p>{step.details.duration}</p>
                    
                    <h4>What to Expect</h4>
                    <p>{step.details.whatToExpect}</p>
                  </>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Right column: Details (desktop only) */}
        <div className="step-details desktop-only">
          <h2>{steps[active].title}</h2>
          
          <div className="detail-section">
            <h3>Overview</h3>
            <p>{steps[active].details.overview}</p>
          </div>

          <div className="detail-section">
            <h3>Key Points</h3>
            <ul>
              {steps[active].details.keyPoints.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>

          <div className="detail-section">
            <h3>Duration</h3>
            <p>{steps[active].details.duration}</p>
          </div>

          <div className="detail-section">
            <h3>What to Expect</h3>
            <p>{steps[active].details.whatToExpect}</p>
          </div>
        </div>
      </div>
      {isInView && (
        <div className="keyboard-hint desktop-only">
          Use ← → keys or click to navigate
        </div>
      )}
    </div>
  );
}

function Technology() {
  const particlesRef = useRef(null);

  useEffect(() => {
    const loadParticles = async () => {
      await import('particles.js');
      if (particlesRef.current && window.particlesJS) {
        window.particlesJS("particles-js", {
          "particles": {
            "number": {
              "value": 325,
              "density": {
                "enable": true,
                "value_area": 800
              }
            },
            "color": {
              "value": "#90E0E0"
            },
            "shape": {
              "type": "circle",
              "stroke": {
                "width": 0,
                "color": "#000000"
              }
            },
            "opacity": {
              "value": 0.3,
              "random": false,
              "anim": {
                "enable": false,
                "speed": 1,
                "opacity_min": 0.1,
                "sync": false
              }
            },
            "size": {
              "value": 1.5,
              "random": true,
              "anim": {
                "enable": false,
                "speed": 40,
                "size_min": 0.1,
                "sync": false
              }
            },
            "line_linked": {
              "enable": true,
              "distance": 200,
              "color": "#90E0E0",
              "opacity": 0.2,
              "width": 1
            },
            "move": {
              "enable": true,
              "speed": 4,
              "direction": "none",
              "random": false,
              "straight": false,
              "out_mode": "out",
              "bounce": false,
              "attract": {
                "enable": false,
                "rotateX": 600,
                "rotateY": 1200
              }
            }
          },
          "interactivity": {
            "detect_on": "canvas",
            "events": {
              "onhover": {
                "enable": true,
                "mode": "grab"
              },
              "onclick": {
                "enable": true,
                "mode": "push"
              },
              "resize": true
            },
            "modes": {
              "grab": {
                "distance": 200,
                "line_linked": {
                  "opacity": 0.4
                }
              },
              "push": {
                "particles_nb": 4
              }
            }
          },
          "retina_detect": true
        });
      }
    };

    loadParticles();

    return () => {
      if (window.pJSDom && window.pJSDom[0]) {
        window.pJSDom[0].pJS.fn.vendors.destroypJS();
        window.pJSDom = [];
      }
    };
  }, []);

  // Add intersection observer for fade-in animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-fade-in');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div id="technology-page">
      <Helmet>
        <title>Technology | NoetherTech</title>
      </Helmet>
      {/* Title Section */}
      <div id="technology-title">
        <div id="particles-js" ref={particlesRef} className="particles-container"></div>
        <div id="titleTextContainer">
          <h1 className="animate-fade-in">ELECTROMAGNETIC BRAIN PULSING™</h1>
        </div>
      </div>

      {/* What is EMBP Section */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="animate-fade-in">What is EMBP™?</h2>
          <p className="animate-fade-in delay-1">Electromagnetic Brain Pulsing (EMBP™) is a breakthrough in neuropsychiatric treatment, offering personalized neuromodulation therapy without medication or invasive procedures. This innovative technology combines precision diagnostics with targeted electromagnetic stimulation to restore and optimize brain function.</p>
        </div>
      </section>

      {/* How EMBP Works Section */}
      <section className="content-section highlight">
        <div className="section-container">
          <h2 className="animate-fade-in">How EMBP™ Works</h2>
          <p className="timeline-subtitle mobile-only">Click the step to learn more</p>
          <ProcessSteps steps={steps} />
        </div>
      </section>

      {/* Clinical Applications Section */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="animate-fade-in">Clinical Applications</h2>
          <p className="animate-fade-in delay-1">EMBP™ has demonstrated significant therapeutic potential in treating multiple neuropsychiatric conditions</p>
          <div className="conditions-grid">
            <div className="condition-item animate-fade-in delay-2">Major Depressive Disorder (MDD)</div>
            <div className="condition-item animate-fade-in delay-2">Anxiety Disorders</div>
            <div className="condition-item animate-fade-in delay-3">Attention Deficit Hyperactivity Disorder (ADHD)</div>
            <div className="condition-item animate-fade-in delay-3">Substance Use Disorder (SUD)</div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="content-section highlight">
        <div className="section-container">
          <h2>Treatment Benefits</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>Personalized Approach</h3>
              <p>Each treatment protocol is customized based on individual brain activity patterns</p>
            </div>
            <div className="benefit-item">
              <h3>Non-Invasive</h3>
              <p>No surgery or anesthesia required</p>
            </div>
            <div className="benefit-item">
              <h3>Drug-Free</h3>
              <p>Achieve therapeutic benefits without pharmaceutical intervention</p>
            </div>
            <div className="benefit-item">
              <h3>Evidence-Based</h3>
              <p>Built on established neurological principles and validated through clinical application</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Technology;
